import * as React from 'react';

import ResizeObserver from 'resize-observer-polyfill';

import {PaginationControls} from 'shells/pagination/pagination-controls';

import {ReportEmptyView} from '../report-empty-view';

import styles from './activity-report.module.css';

type Props = {
    hasError: boolean;
    isLoading: boolean;
    content: string | null | undefined;
    fetchContent: (...args: any) => any;
    filters: any;
    totalPages: number;
    selectedPage: number;
    stickyHeaderTop: number | null | undefined;
    onParamPush: (arg1: any, arg2: boolean) => any;
};

type State = {
    activityCountText: string | null | undefined;
};

/*
 * Component representing the Overview tab of the Activity Report.
 *
 * The main content of this tab is currently drawn from a PJAX request stored in the store's activityReport.overview
 * property.
 */
export class ActivityReportLegacyTab extends React.Component<Props, State> {
    pageContentDiv: HTMLElement | null | undefined;
    paginationHeader: HTMLElement | null | undefined;
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    width = 800;

    static defaultProps = {
        selectedPage: 1,
    };

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            activityCountText: null,
        };
    }

    UNSAFE_componentWillMount() {
        this.props.fetchContent(this.props.filters, this.props.selectedPage);
    }

    componentDidMount() {
        // @ts-expect-error
        this.pageContentDiv = document.querySelector('#report-content-scroll-container');

        if (!this.pageContentDiv) {
            // @ts-expect-error
            if (typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.console.info(
                    'Legacy activity report, report-content-scroll-container not found'
                );
            }
            // Try using a different div
            // @ts-expect-error
            this.pageContentDiv = document.querySelector('.routed-report-inner-container');
            if (!this.pageContentDiv) return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.pageContentDiv) {
                    const cr = entry.contentRect;
                    if (cr.width !== this.width) {
                        this.width = cr.width;
                        this.forceUpdate();
                    }
                }
            }
        });

        if (this.pageContentDiv && this.pageContentDiv instanceof HTMLElement) {
            this.resizeObserver.observe(this.pageContentDiv);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            JSON.stringify(nextProps.filters) !== JSON.stringify(this.props.filters) ||
            nextProps.selectedPage !== this.props.selectedPage
        ) {
            this.props.fetchContent(nextProps.filters, nextProps.selectedPage);

            // Scroll back to the top if a new page of content has come in, and we have a stickyHeader
            const tabsDiv = document.querySelector('#report-tabs');
            if (tabsDiv && this.pageContentDiv && this.props.stickyHeaderTop) {
                // @ts-expect-error
                this.pageContentDiv.scrollTop = tabsDiv.offsetTop + tabsDiv.offsetHeight + 1;
            }
        }

        // This is a hack to grab the activity count text from the string of html
        // we are getting from the server.  We want to pull out that text and put
        // it in the pagination control bar
        if (nextProps.content !== this.props.content) {
            const content = document.createElement('div');
            if (nextProps.content) {
                content.innerHTML = nextProps.content;
                const activityCountNode = content.querySelector('.detail-table-activity-count');
                if (activityCountNode) {
                    this.setState({activityCountText: activityCountNode.innerHTML});
                }
            }
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.stickyHeaderTop !== prevProps.stickyHeaderTop) {
            if (this.paginationHeader && this.props.stickyHeaderTop) {
                this.paginationHeader.style.position = 'fixed';
                this.paginationHeader.style.top = `${this.props.stickyHeaderTop}px`;
                this.paginationHeader.style.width = `${this.width - 16}px`; // account for 16px left padding
                this.paginationHeader.style.borderBottom = '1px solid #e1e1e1';
            } else if (this.paginationHeader) {
                this.paginationHeader.style.position = 'relative';
                this.paginationHeader.style.top = '0';
                this.paginationHeader.style.width = '';
                this.paginationHeader.style.borderBottom = '';
            }
        }

        // Update the pagination header width if the width has changed (eg. window was resized)
        if (this.paginationHeader && this.paginationHeader.clientWidth !== this.width) {
            this.paginationHeader.style.width = `${this.width - 16}px`; // account for 16px left padding
        }
    }

    render() {
        const legacyContentDiv = this.props.content ? (
            <div dangerouslySetInnerHTML={{__html: this.props.content}} />
        ) : (
            <div />
        );

        return (
            <div className={styles['legacy-table-container']}>
                {this.props.isLoading || this.props.hasError ? (
                    <ReportEmptyView
                        isLoading={this.props.isLoading}
                        onErrorRetry={this.handleErrorRetry}
                        hasError={this.props.hasError}
                    />
                ) : undefined}
                <div>
                    {this.props.stickyHeaderTop ? (
                        <div
                            className='placeholder'
                            style={{
                                height: this.paginationHeader
                                    ? this.paginationHeader.clientHeight
                                    : 42,
                            }}
                        />
                    ) : null}
                    <div
                        ref={(node) => {
                            this.paginationHeader = node;
                        }}
                        className={styles['pagination-header']}
                    >
                        {this.state.activityCountText ? (
                            <div>{this.state.activityCountText}</div>
                        ) : null}
                        <PaginationControls
                            totalPages={this.props.totalPages}
                            selectedPage={this.props.selectedPage}
                            onPageChange={this.handlePageChange}
                        />
                    </div>
                </div>
                <div className={styles['legacy-container']}>{legacyContentDiv}</div>
            </div>
        );
    }

    handlePageChange = (page: number) => {
        this.props.onParamPush({page: page}, false);
    };

    handleErrorRetry = () => this.props.fetchContent(this.props.filters, this.props.selectedPage);
}
